import { parse } from '../libs/webvttnotes/parser';

/**
 * Process raw VTT content into structured subtitle objects using the existing parser
 * @param {string} rawVtt - Raw VTT file content
 * @returns {Array} Array of subtitle objects with consistent ID format
 */
export const processVttContent = (rawVtt) => {
  if (!rawVtt) return [];
  
  try {
    // Use the existing parse function from parser.js
    const parsedContent = parse(rawVtt);
    
    // Check what type of data was returned
    if (!parsedContent) return [];
    
    // If parsedContent is not an array, handle it appropriately
    const cueList = Array.isArray(parsedContent) ? parsedContent : 
                   (parsedContent.cues || parsedContent.captions || []);
    
    // Ensure each subtitle has a consistent ID and structure
    return cueList.map((cue, index) => ({
      id: cue.id || index + 1,
      timecode: `${formatTimestamp(cue.start)} --> ${formatTimestamp(cue.end)}`,
      start: cue.start,
      end: cue.end,
      text: cue.text ? (Array.isArray(cue.text) ? cue.text : [cue.text]) : [],
      settings: cue.settings || {}
    }));
  } catch (error) {
    console.error('Error parsing VTT content:', error);
    return [];
  }
};

/**
 * Format timestamp in VTT format (hh:mm:ss.mmm)
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted timestamp
 */
export const formatTimestamp = (seconds) => {
  if (seconds === undefined || seconds === null) return '00:00:00.000';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const milliseconds = Math.floor((seconds % 1) * 1000);
  
  return `${hours.toString().padStart(2, '0')}:${
    minutes.toString().padStart(2, '0')}:${
    secs.toString().padStart(2, '0')}.${
    milliseconds.toString().padStart(3, '0')}`;
};

/**
 * Compare two VTT contents and identify differences
 * @param {Array} oldContent - Array of subtitle objects from the old version
 * @param {Array} newContent - Array of subtitle objects from the new version
 * @returns {Array} Array of difference objects
 */
export const compareVttContents = (oldContent, newContent) => {
  const results = [];

  // Normalizes text for comparison: preserve line breaks but normalize CRLF, remove zero-width chars, and collapse tabs/spaces
  const normalizeText = (textOrArray) => {
    const raw = Array.isArray(textOrArray) ? textOrArray.join('\n') : (textOrArray || '');
    // remove common invisible characters and BOM
    const cleaned = raw.replace(/\uFEFF|\u200B/g, '');
    // normalize CRLF -> LF
    const lf = cleaned.replace(/\r\n?/g, '\n');
    // collapse multiple spaces/tabs but keep newlines
    return lf.split('\n').map(line => line.replace(/[\t ]+/g, ' ').trim()).join('\n').trim();
  };

  // Small epsilon for time comparisons (seconds)
  const TIME_EPSILON = 0.001;

  // Create maps for faster lookup (use string keys to be consistent)
  const oldMap = new Map(oldContent.map(item => [String(item.id), item]));
  const newMap = new Map(newContent.map(item => [String(item.id), item]));

  // Find all unique IDs
  const allIds = new Set([...oldMap.keys(), ...newMap.keys()]);

  // Compare each subtitle
  for (const id of allIds) {
    const oldItem = oldMap.get(id);
    const newItem = newMap.get(id);

    // Case 1: Subtitle was added
    if (!oldItem && newItem) {
      results.push({ id, type: 'added', newItem });
      continue;
    }

    // Case 2: Subtitle was removed
    if (oldItem && !newItem) {
      results.push({ id, type: 'removed', oldItem });
      continue;
    }

    // Case 3: Subtitle was changed or unchanged
    if (oldItem && newItem) {
      const oldText = normalizeText(oldItem.text);
      const newText = normalizeText(newItem.text);

      const startOld = typeof oldItem.start === 'number' ? oldItem.start : parseFloat(oldItem.start || 0) || 0;
      const endOld = typeof oldItem.end === 'number' ? oldItem.end : parseFloat(oldItem.end || 0) || 0;
      const startNew = typeof newItem.start === 'number' ? newItem.start : parseFloat(newItem.start || 0) || 0;
      const endNew = typeof newItem.end === 'number' ? newItem.end : parseFloat(newItem.end || 0) || 0;

      const timecodeChanged = Math.abs(startOld - startNew) > TIME_EPSILON || Math.abs(endOld - endNew) > TIME_EPSILON;

      if (oldText !== newText || timecodeChanged) {
        results.push({
          id,
          type: 'changed',
          oldItem,
          newItem,
          changes: { text: oldText !== newText, timing: timecodeChanged },
        });
      } else {
        // No changes
        results.push({ id, type: 'unchanged', oldItem, newItem });
      }
    }
  }

  // If no per-cue differences were detected but the concatenated file text differs,
  // produce a single 'changed' item covering the whole file. This handles cases
  // where phrases were merged/split or cue IDs changed so cue-level matching missed them.
  const nonUnchanged = results.filter((r) => r.type !== 'unchanged');
  if (nonUnchanged.length === 0) {
    const oldFull = (oldContent || []).map((i) => normalizeText(i.text)).join('\n').trim();
    const newFull = (newContent || []).map((i) => normalizeText(i.text)).join('\n').trim();

    if (oldFull !== newFull) {
      const oldItem = {
        id: 'file-old',
        text: oldFull ? oldFull.split('\n') : [],
        start: oldContent[0]?.start || 0,
        end: oldContent[oldContent.length - 1]?.end || 0,
      };

      const newItem = {
        id: 'file-new',
        text: newFull ? newFull.split('\n') : [],
        start: newContent[0]?.start || 0,
        end: newContent[newContent.length - 1]?.end || 0,
      };

      return [
        {
          id: 'file',
          type: 'changed',
          oldItem,
          newItem,
          changes: { text: true, timing: true },
        },
      ];
    }
  }

  // Sort by numeric ID when possible to maintain subtitle order
  return results.sort((a, b) => {
    const idA = Number(a.id) || 0;
    const idB = Number(b.id) || 0;
    return idA - idB;
  });
};

/**
 * Check if a string is numeric
 * @param {string} str - String to check
 * @returns {boolean} True if numeric
 */
export const isNumeric = (str) => {
  if (str === undefined || str === null) return false;
  return !isNaN(str) && !isNaN(parseFloat(str));
};

/**
 * Convert VTT timecode to seconds for comparison
 * @param {string} timecode - VTT timecode
 * @returns {number} Time in seconds
 */
export const parseTimecode = (timecode) => {
  if (!timecode) return 0;
  
  try {
    const parts = timecode.split(':');
    let seconds;
    
    if (parts.length === 3) {
      // Format: hh:mm:ss.mmm
      seconds = 
        parseFloat(parts[0]) * 3600 + 
        parseFloat(parts[1]) * 60 + 
        parseFloat(parts[2]);
    } else if (parts.length === 2) {
      // Format: mm:ss.mmm
      seconds = parseFloat(parts[0]) * 60 + parseFloat(parts[1]);
    } else {
      // Format: ss.mmm
      seconds = parseFloat(timecode);
    }
    
    return seconds || 0;
  } catch (error) {
    console.error('Error parsing timecode:', timecode, error);
    return 0;
  }
};

// Add this utility function inside your component or import it from vttUtils.js
export const formatVttTime = (seconds) => {
    if (seconds === undefined || seconds === null) return '00:00:00.000';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const milliseconds = Math.round((seconds % 1) * 1000);
    
    return `${hours.toString().padStart(2, '0')}:${
      minutes.toString().padStart(2, '0')}:${
      secs.toString().padStart(2, '0')}.${
      milliseconds.toString().padStart(3, '0')}`;
  };
  
  // Add this function to generate a timecode string from start and end times
  export const generateTimecode = (item) => {
    if (item) {
      const start = item.start || 0;
      const end = item.end || 0;
      return `${formatVttTime(start)} --> ${formatVttTime(end)}`;
    }
    return '00:00:00.000 --> 00:00:00.000';
  };